'use client';
import { Fragment, useState } from 'react';
import {
  Dialog,
  Popover,
  Transition,
  PopoverButton,
  DialogPanel,
  PopoverGroup,
  PopoverPanel,
} from '@headlessui/react';
import {
  ArrowPathIcon,
  Bars3Icon,
  ChartPieIcon,
  CursorArrowRaysIcon,
  FingerPrintIcon,
  SquaresPlusIcon,
  XMarkIcon,
  BuildingOffice2Icon,
  ComputerDesktopIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';
import {
  ChevronUpIcon,
  ChevronDownIcon,
  CpuChipIcon,
} from '@heroicons/react/20/solid';

import logo from '@/app/logo.webp';
import partnerLogo from '@/app/Anacle-Logo-Website-Partner.svg';
import config from '@/config';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { MENU_ITEMS as REAL_ESTATE_MENU_ITEMS } from '../app/real-estate/secondaryMenuItems';
import { MENU_ITEMS as DIGITAL_WORKPLACE_MENU_ITEMS } from '../app/digital-workplace/secondaryMenuItems';
import { MENU_ITEMS as UTILITIES_MENU_ITEMS } from '../app/utilities/secondaryMenuItems';
import { MENU_ITEMS as IOT_MENU_ITEMS } from '../app/iot/secondaryMenuItems';
import { menuItem } from '@/components/SecondaryMenu';
import { useRouter } from 'next/navigation';
import { Search } from 'lucide-react';
// Add this type definition before the subMenuConfig
type SubMenuConfigKey = keyof typeof subMenuConfig;

const simplicity = [
  {
    name: 'Simplicity Software Architecture',
    href: '/',
    icon: ChartPieIcon,
  },
  {
    name: 'Real Estate',
    href: '/real-estate',
    icon: BuildingOffice2Icon,
  },
  {
    name: 'Digital Workplace',
    href: '/digital-workplace',
    icon: ComputerDesktopIcon,
  },
  {
    name: 'Utilities',
    href: '/utilities',
    icon: WrenchScrewdriverIcon,
  },
  {
    name: 'IoT',
    href: '/iot',
    icon: CpuChipIcon,
  },
  {
    name: 'Partners',
    href: '/partners-network',
    icon: ChartPieIcon,
  },
];

const realEstate = [
  {
    name: 'Real Estate (RE) Solution',
    href: '/real-estate',
    icon: ChartPieIcon,
  },
  {
    name: 'Financial Management',
    href: '/real-estate/financial-management',
    icon: ChartPieIcon,
  },
  {
    name: 'Customer Relationship Management',
    href: '/real-estate/customer-relationship-management',
    icon: CursorArrowRaysIcon,
  },
  {
    name: 'Real Estate Lease Management',
    href: '/real-estate/real-estate-lease-management',
    icon: FingerPrintIcon,
  },
  {
    name: 'Utilities Management',
    href: '/real-estate/utilities-management',
    icon: SquaresPlusIcon,
  },
  {
    name: 'Shared Resources Management',
    href: '/real-estate/shared-resources-management',
    icon: ArrowPathIcon,
  },
  {
    name: 'Supply Chain Management',
    href: '/real-estate/supply-chain-management',
    icon: ArrowPathIcon,
  },
  {
    name: 'Building Asset & Maintenance Management',
    href: '/real-estate/building-asset-and-maintenance-management',
    icon: ArrowPathIcon,
  },
  {
    name: 'Parking Management',
    href: '/real-estate/parking-management',
    icon: ArrowPathIcon,
  },
  {
    name: 'Internet Portals',
    href: '/real-estate/internet-portals',
    icon: ArrowPathIcon,
  },
  {
    name: 'Mobile Apps',
    href: '/real-estate/mobile-apps',
    icon: ArrowPathIcon,
  },
  {
    name: 'Common Utilities - Decision Support Modules',
    href: '/real-estate/decision-support-modules',
    icon: ArrowPathIcon,
  },
  {
    name: 'Common Utilities - Application Configuration Tools',
    href: '/real-estate/application-configuration-tools',
    icon: ArrowPathIcon,
  },
  {
    name: 'Application Programming Interfaces',
    href: '/real-estate/application-programming-interfaces',
    icon: ArrowPathIcon,
  },
];

const dwp = [
  {
    name: 'Digital Workplace (DW) Solution',
    href: '/digital-workplace',
    icon: ChartPieIcon,
  },
  {
    name: 'Space Management',
    href: '/digital-workplace/space-management',
    icon: ChartPieIcon,
  },
  {
    name: 'Real Estate Lease Management',
    href: '/digital-workplace/real-estate-lease-management',
    icon: CursorArrowRaysIcon,
  },
  {
    name: 'Utilities Management',
    href: '/digital-workplace/utilities-management',
    icon: CursorArrowRaysIcon,
  },
  {
    name: 'Shared Resources Management',
    href: '/digital-workplace/shared-resource-management',
    icon: FingerPrintIcon,
  },
  {
    name: 'Supply Chain Management',
    href: '/digital-workplace/supply-chain-management',
    icon: ArrowPathIcon,
  },
  {
    name: 'Building Asset & Maintenance Management',
    href: '/digital-workplace/building-asset-and-maintenance-management',
    icon: ArrowPathIcon,
  },
  {
    name: 'Parking Management',
    href: '/digital-workplace/parking-management',
    icon: ArrowPathIcon,
  },
  {
    name: 'Internet Portals',
    href: '/digital-workplace/internet-portals',
    icon: ArrowPathIcon,
  },
  {
    name: 'Mobile Apps',
    href: '/digital-workplace/mobile-apps',
    icon: ArrowPathIcon,
  },
  {
    name: 'Common Utilities - Decision Support Modules',
    href: '/digital-workplace/decision-support-modules',
    icon: ArrowPathIcon,
  },
  {
    name: 'Common Utilities - Application Configuration Tools',
    href: '/digital-workplace/application-configuration-tools',
    icon: ArrowPathIcon,
  },
  {
    name: 'Application Programming Interfaces',
    href: '/digital-workplace/application-programming-interfaces',
    icon: ArrowPathIcon,
  },
];

const utilities = [
  {
    name: 'Utilities (UT) Solution',
    href: '/utilities',
    icon: ChartPieIcon,
  },
  {
    name: 'Financial Management',
    href: '/utilities/financial-management',
    icon: CursorArrowRaysIcon,
  },
  {
    name: 'Customer Relationship Management',
    href: '/utilities/customer-relationship-management',
    icon: FingerPrintIcon,
  },
  {
    name: 'Utilities Management',
    href: '/utilities/utilities-management',
    icon: SquaresPlusIcon,
  },
  {
    name: 'Auto Metering Infrastructure',
    href: '/utilities/auto-metering-infrastructure',
    icon: ArrowPathIcon,
  },
  {
    name: 'Supply Chain Management',
    href: '/utilities/supply-chain-management',
    icon: ArrowPathIcon,
  },
  {
    name: 'Asset & Maintenance Management',
    href: '/utilities/asset-and-maintenance-management',
    icon: ArrowPathIcon,
  },
  {
    name: 'Internet Portals',
    href: '/utilities/internet-portals',
    icon: ArrowPathIcon,
  },
  {
    name: 'Mobile Apps',
    href: '/utilities/mobile-apps',
    icon: ArrowPathIcon,
  },
  {
    name: 'Common Utilities - Decision Support Modules',
    href: '/utilities/decision-support-modules',
    icon: ArrowPathIcon,
  },
  {
    name: 'Common Utilities - Application Configuration Tools',
    href: '/utilities/application-configuration-tools',
    icon: ArrowPathIcon,
  },
  {
    name: 'Application Programming Interfaces',
    href: '/utilities/application-programming-interfaces',
    icon: ArrowPathIcon,
  },
];

const partners = [
  {
    name: 'Our Simplicity Partner Network',
    href: '/partners/partner-network',
    icon: ChartPieIcon,
  },
  {
    name: 'Our Channel Partner Programs',
    href: '/partners/channel-partner-programs',
    icon: CursorArrowRaysIcon,
  },
  {
    name: 'Preferred VAR',
    href: '/partners/preferred-var',
    icon: FingerPrintIcon,
  },
  {
    name: 'Authorised VAR',
    href: '/partners/authorised-var',
    icon: SquaresPlusIcon,
  },
  {
    name: 'Exclusive VAR',
    href: '/partners/exclusive-var',
    icon: ArrowPathIcon,
  },
  {
    name: 'Professional Services Providers ',
    href: '/partners/professional-services-providers',
    icon: ArrowPathIcon,
  },
];

const iot = [
  {
    name: 'Internet Of Things (IoT) Solution',
    href: '/iot',
    icon: ChartPieIcon,
  },
  {
    name: 'Asset & Infrastructure Monitoring',
    href: '/iot/asset-infrastructure-monitoring',
    icon: ChartPieIcon,
  },
  {
    name: 'Energy & Environment Management',
    href: '/iot/energy-environment-management',
    icon: ChartPieIcon,
  },
  {
    name: 'Space and Occupancy Intelligence',
    href: '/iot/space-occupancy-intelligence',
    icon: ChartPieIcon,
  },
  {
    name: 'IoT Security & Risk Management',
    href: '/iot/iot-security-risk-management',
    icon: ChartPieIcon,
  },
  {
    name: 'Smart Automation Controls',
    href: '/iot/smart-automation-controls',
    icon: ChartPieIcon,
  },
];

// Combine all menu items with their respective categories for search
const allItems = [
  ...realEstate.map((item) => ({ ...item, category: 'Real Estate' })),
  ...dwp.map((item) => ({ ...item, category: 'Digital Workplace' })),
  ...utilities.map((item) => ({ ...item, category: 'Utilities' })),
  ...iot.map((item) => ({ ...item, category: 'IoT' })),
  ...partners.map((item) => ({ ...item, category: 'Partners' })),
];

// Helper function to render submenu
const renderSubmenu = (menuItems: readonly menuItem[]) => {
  return (
    <ul className="ml-6 mt-4 mb-4">
      {menuItems.map((item) => {
        if (!item.items) {
          return (
            <li id={item.href} key={item.href} className="pb-4">
              <Link href={item.href}>{item.title}</Link>
            </li>
          );
        } else {
          return (
            <Fragment key={item.title}>
              {item.items.map((subItem) => (
                <li id={subItem.href} key={subItem.href} className="pb-4">
                  <Link href={subItem.href}>
                    {item.title + ' - ' + subItem.title}
                  </Link>
                </li>
              ))}
            </Fragment>
          );
        }
      })}
    </ul>
  );
};

// Submenu configuration
const subMenuConfig = {
  'Real Estate': {
    items: [
      { title: 'Real Estate(RE) Solution', href: '/real-estate' },
      ...REAL_ESTATE_MENU_ITEMS,
    ],
  },
  'Digital Workplace': {
    items: [
      { title: 'Digital Workplace(DW) Solution', href: '/digital-workplace' },
      ...DIGITAL_WORKPLACE_MENU_ITEMS,
    ],
  },
  Utilities: {
    items: [
      { title: 'Simplicity® Utilities(UT) Solution', href: '/utilities' },
      ...UTILITIES_MENU_ITEMS,
    ],
  },
  IoT: {
    items: [
      { title: 'Simplicity® Internet Of Things(IoT) Solution', href: '/iot' },
      ...IOT_MENU_ITEMS,
    ],
  },
  Partners: {
    items: [
      ...partners.map((item) => ({
        title: item.name,
        href: item.href,
      })),
    ],
  },
} as const;

// Add a helper function to check if a key exists in subMenuConfig
const isValidSubMenuKey = (key: string): key is SubMenuConfigKey => {
  return key in subMenuConfig;
};

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const pathname = usePathname();
  const isPartnersPage = pathname.startsWith('/partners');

  // Filter items based on a search query
  const filteredItems = allItems.filter((item) =>
    item.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <header className="bg-white fixed z-30 w-full shadow-sm">
      <nav
        className="mx-auto flex px-4 xl:px-0 xl:max-w-8xl items-center justify-between py-2"
        aria-label="Global"
      >
        <div className="flex justify-start lg:flex-0 w-[150px] xl:w-[180px] border-0 lg:border-r-2 border-gray-200 ">
          <Link
            className="-m-1.5 p-1.5"
            href="https://anacle.com/"
            title={`${config.appName} homepage`}
          >
            <span className="sr-only">Anacle</span>
            <Image
              src={isPartnersPage ? partnerLogo : logo}
              alt={`${config.appName} logo`}
              className="w-32"
              priority={true}
              width={225}
              height={225}
            />
          </Link>
        </div>
        <PopoverGroup className="hidden lg:flex lg:flex-1 gap-x-4 xl:gap-x-8 justify-between ml-4 xl:ml-8 h-[64px] items-center">
          <Link
            href="/"
            className={`transition duration-500 hover:bg-gray-100 p-2`}
          >
            <span
              className={`py-1 px-1 border-0 leading-6 ${pathname === '/' ? 'drop-shadow-sm text-primary font-semibold border-b-2 border-b-cyan-500' : 'text-gray-900'}`}
            >
              Simplicity<sup>®</sup>
            </span>
          </Link>
          <DropdownMenu
            title="Real Estate"
            items={realEstate}
            activeColor={isPartnersPage}
          />
          <DropdownMenu
            title="Digital Workplace"
            items={dwp}
            activeColor={isPartnersPage}
          />
          <DropdownMenu
            title="Utilities"
            items={utilities}
            activeColor={isPartnersPage}
          />
          <DropdownMenu title="IoT" items={iot} activeColor={isPartnersPage} />
          <DropdownMenu
            title="Partner"
            items={partners}
            activeColor={isPartnersPage}
          />

          <Link
            href="https://anacle.com/contact-us/"
            className={`transition duration-500 hover:bg-gray-100 p-2`}
          >
            <span
              className={`py-1 px-1 border-0 leading-6 text-gray-900 mr-10`}
            >
              Contact Us
            </span>
          </Link>
          {/* <a href="#" className="py-2 text-gray-900">
            <LuSearch size={18} />
          </a> */}
        </PopoverGroup>
        <div className="flex gap-0">
          <Search
            className="w-5 h-5 cursor-pointer mr-5"
            onClick={() => setIsSearchOpen(true)}
          />
          <div className="flex lg:hidden">
            <button
              type="button"
              className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-gray-700"
              onClick={() => setMobileMenuOpen(true)}
            >
              <span className="sr-only">Open main menu</span>
              <Bars3Icon id="bar3icon" className="size-6" aria-hidden="true" />
            </button>
          </div>
        </div>
      </nav>
      <MobileMenu
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
      />
      {/* Search modal */}
      {isSearchOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-start justify-center pt-20">
          <div className="bg-white w-full max-w-lg p-6 rounded-lg shadow-lg">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Search</h2>
              <button
                onClick={() => setIsSearchOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            <input
              type="text"
              placeholder="Search categories..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-cyan-500"
            />
            <ul className="space-y-2 max-h-[300px] overflow-y-auto">
              {filteredItems.length > 0 ? (
                filteredItems.map((item) => (
                  <li key={item.href}>
                    <Link
                      href={item.href}
                      className="block px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
                      onClick={() => setIsSearchOpen(false)}
                    >
                      <span className="font-medium">{item.name}</span>
                      <span className="text-sm text-gray-500 ml-2">
                        ({item.category})
                      </span>
                    </Link>
                  </li>
                ))
              ) : (
                <li className="text-gray-500 px-4 py-2">No results found</li>
              )}
            </ul>
          </div>
        </div>
      )}
    </header>
  );
}

// @ts-ignore
function DropdownMenu({ title, items, activeColor }) {
  const pathname = usePathname();
  const active = items.find((item: { href: string }) => item.href === pathname);
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Popover className="relative">
      {({ open, close }) => (
        <>
          <div
            onMouseEnter={() => setIsOpen(true)}
            onMouseLeave={() => setIsOpen(false)}
          >
            <PopoverButton
              className={`outline-0 hover:bg-gray-100 h-[40px] hover:text-primary hover:drop-shadow-sm flex p-2 items-center gap-x-1 leading-6`}
              onClick={(e) => e.preventDefault()}
            >
              <span
                className={`leading-6 py-1 px-1 border-0 ${
                  active
                    ? activeColor
                      ? 'border-b-2 border-b-[#BF984D] text-[#BF984D] font-semibold'
                      : 'border-b-2 border-b-cyan-500 text-primary font-semibold'
                    : 'text-gray-900'
                }`}
              >
                {title}
              </span>
              <ChevronDownIcon
                className="h-5 w-5 flex-none text-gray-400"
                aria-hidden="true"
              />
            </PopoverButton>
            <Transition
              show={isOpen}
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <PopoverPanel
                static
                className="absolute -left-8 top-full z-30 mt-3 w-screen max-w-md overflow-auto bg-white shadow-lg ring-1 ring-gray-900/5"
              >
                <div className="px-2 py-4">
                  {items.map((item: any) => (
                    <div
                      key={item.name}
                      className="group relative flex gap-x-6 p-3 text-sm leading-6 hover:bg-gray-100"
                    >
                      {/*<div className="mt-1 flex h-11 w-11 flex-none items-center justify-center rounded-lg bg-gray-50 group-hover:bg-white">*/}
                      {/*  <item.icon className="text-gray-600 group-hover:text-indigo-600" aria-hidden="true" />*/}
                      {/*</div>*/}
                      <div className="flex-auto">
                        <Link
                          href={item.href}
                          className="block font-medium text-gray-900 text-sm"
                        >
                          {item.name}
                          <span className="absolute inset-0" />
                        </Link>
                        {/*<p className="mt-1 text-gray-600">{item.description}</p>*/}
                      </div>
                    </div>
                  ))}
                </div>
                {/* <div className="grid grid-cols-2 divide-x divide-gray-900/5 bg-gray-50">
                  {callsToAction.map((item) => (
                    <a
                      key={item.name}
                      href={item.href}
                      className="flex items-center justify-center gap-x-2.5 p-3 text-sm font-semibold leading-6 text-gray-900 hover:bg-gray-100"
                    >
                      <item.icon
                        className="h-5 w-5 flex-none text-gray-400"
                        aria-hidden="true"
                      />
                      {item.name}
                    </a>
                  ))}
                </div> */}
              </PopoverPanel>
            </Transition>
          </div>
        </>
      )}
    </Popover>
  );
}

// @ts-ignore
function MobileMenu({ mobileMenuOpen, setMobileMenuOpen }) {
  const router = useRouter();
  const [expandedItem, setExpandedItem] = useState<string>();

  const toggleExpand = (itemName: string, itemHref: string) => {
    if (isValidSubMenuKey(itemName)) {
      // Toggle submenu expansion
      setExpandedItem(expandedItem === itemName ? null : itemName);
    } else {
      // Navigate to the link for items without a submenu
      setExpandedItem(null);
      router.push(itemHref);
    }
  };
  return (
    <Dialog
      className="lg:hidden"
      open={mobileMenuOpen}
      onClose={setMobileMenuOpen}
    >
      <div className="fixed inset-0 z-10" />
      <DialogPanel className="fixed inset-y-0 right-0 z-40 flex w-full flex-col justify-between overflow-y-auto bg-white sm:max-w-sm sm:ring-1 sm:ring-gray-900/10">
        <div className="pt-2 px-4">
          <div className="flex items-center justify-between">
            <Link
              className="-m-1.5 p-1.5"
              href="https://anacle.com/"
              title={`${config.appName} homepage`}
            >
              <span className="sr-only">Anacle</span>
              <Image
                src={logo}
                alt={`${config.appName} logo`}
                className="w-32"
                priority={true}
                width={225}
                height={225}
              />
            </Link>
            <button
              type="button"
              className="-m-2.5 rounded-md p-2.5 text-gray-700"
              onClick={() => setMobileMenuOpen(false)}
            >
              <span className="sr-only">Close menu</span>
              <XMarkIcon aria-hidden="true" className="size-6" />
            </button>
          </div>
          <div className="mt-6 flow-root">
            <div className="-my-6 divide-y divide-gray-500/10">
              <div className="space-y-3 py-6">
                {simplicity.map((item) => (
                  <div
                    key={item.href}
                    id={item.href}
                    className="group -mx-3 flex items-center gap-x-1 p-2 bg-gray-100 text-xl leading-7 text-gray-600"
                  >
                    <div className="w-full">
                      <div
                        className="flex px-2 py-2 w-full justify-between cursor-pointer"
                        onClick={() => toggleExpand(item.name, item.href)}
                      >
                        <span>{item.name}</span>
                        {item.name in subMenuConfig && (
                          <button className="text-gray-600">
                            {expandedItem === item.name ? (
                              <ChevronUpIcon
                                className="w-10 pl-3"
                                aria-hidden="true"
                              />
                            ) : (
                              <ChevronDownIcon
                                className="w-10 pl-3"
                                aria-hidden="true"
                              />
                            )}
                          </button>
                        )}
                      </div>
                      {expandedItem === item.name &&
                        isValidSubMenuKey(item.name) &&
                        renderSubmenu(subMenuConfig[item.name].items)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </DialogPanel>
    </Dialog>
  );
}
